import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  CircularProgress,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Tabs,
  Tab,
} from "@mui/material";
import { SelectChangeEvent } from "@mui/material";
import { Link } from "react-router-dom";

interface Currency {
  id: number;
  name: string;
  buy: number;
  sell: number;
  abbreviation: string;
  icon_link: string;
}

const convertToEnglishNumbers = (input: string): string => {
  const persianNumbers = [
    /۰/g,
    /۱/g,
    /۲/g,
    /۳/g,
    /۴/g,
    /۵/g,
    /۶/g,
    /۷/g,
    /۸/g,
    /۹/g,
  ];
  const arabicNumbers = [
    /٠/g,
    /١/g,
    /٢/g,
    /٣/g,
    /٤/g,
    /٥/g,
    /٦/g,
    /٧/g,
    /٨/g,
    /٩/g,
  ];

  let converted = input;
  persianNumbers.forEach((regex, index) => {
    converted = converted.replace(regex, index.toString());
  });
  arabicNumbers.forEach((regex, index) => {
    converted = converted.replace(regex, index.toString());
  });

  return converted;
};

const FiatForm: React.FC = () => {
  const [currencies, setCurrencies] = useState<Currency[]>([]);
  const [selectedCurrency, setSelectedCurrency] = useState<Currency | null>(
    null
  );
  const [amount, setAmount] = useState<string>(""); // مقدار پرداختی
  const [receiveAmount, setReceiveAmount] = useState<string>(""); // مقدار دریافتی
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<number>(0); // 0: خرید, 1: فروش

  useEffect(() => {
    const fetchCurrencies = async () => {
      try {
        const response = await fetch(
          "https://api-panel.parsianbit.com/publish/v1/currency"
        );
        const data = await response.json();
        const currenciesData = data.data.currencies as Currency[];
        setCurrencies(currenciesData);
        setSelectedCurrency(currenciesData[0]); // ارز پیش‌فرض
        setLoading(false);
      } catch (err) {
        setError("خطا در دریافت داده‌ها");
        setLoading(false);
      }
    };

    fetchCurrencies();
  }, []);

  const handleCurrencyChange = (event: SelectChangeEvent<string>) => {
    const currencyId = Number(event.target.value);
    const currency = currencies.find((cur) => cur.id === currencyId) || null;

    setSelectedCurrency(currency);

    if (currency && receiveAmount) {
      const calculatedAmount = (
        parseFloat(receiveAmount) *
        (activeTab === 0 ? currency.buy : currency.sell)
      ).toFixed(0);
      setAmount(calculatedAmount);
    }
  };

  const handleAmountChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const inputAmount = convertToEnglishNumbers(event.target.value);

    if (!/^\d*\.?\d*$/.test(inputAmount)) return;

    if (
      !selectedCurrency ||
      isNaN(Number(inputAmount)) ||
      Number(inputAmount) <= 0
    ) {
      setAmount(inputAmount);
      setReceiveAmount("");
      return;
    }

    setAmount(inputAmount);

    const price =
      activeTab === 0
        ? selectedCurrency.buy // **برای خرید از buy**
        : selectedCurrency.sell; // **برای فروش از sell**

    const calculatedReceiveAmount = (parseFloat(inputAmount) / price).toFixed(
      6
    );
    setReceiveAmount(calculatedReceiveAmount);
  };

  const handleReceiveAmountChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const inputReceiveAmount = convertToEnglishNumbers(event.target.value);

    if (!/^\d*\.?\d*$/.test(inputReceiveAmount)) return;

    if (
      !selectedCurrency ||
      isNaN(Number(inputReceiveAmount)) ||
      Number(inputReceiveAmount) <= 0
    ) {
      setReceiveAmount(inputReceiveAmount);
      setAmount("");
      return;
    }

    setReceiveAmount(inputReceiveAmount);

    const price =
      activeTab === 0 ? selectedCurrency.buy : selectedCurrency.sell;

    const calculatedAmount = (parseFloat(inputReceiveAmount) * price).toFixed(
      0
    );
    setAmount(calculatedAmount);
  };

  const handleTabChange = (newValue: number) => {
    setActiveTab(newValue);

    if (selectedCurrency && receiveAmount) {
      const price =
        newValue === 0 ? selectedCurrency.buy : selectedCurrency.sell;
      const calculatedAmount = (parseFloat(receiveAmount) * price).toFixed(0);
      setAmount(calculatedAmount);
    }
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return <Typography>{error}</Typography>;
  }

  return (
    <Box sx={{ mt: 4 }}>
      <Tabs
        value={activeTab}
        onChange={(e, newValue) => handleTabChange(newValue)}
        centered
        sx={{ bgcolor: "#FF3DFF", color: "#fff" }}
        indicatorColor="primary"
      >
        <Tab label="خرید" />
        <Tab label="فروش" />
      </Tabs>

      <FormControl fullWidth sx={{ mt: 3, mb: 3 }}>
        <InputLabel id="currency-select-label">انتخاب ارز</InputLabel>
        <Select
          labelId="currency-select-label"
          value={selectedCurrency?.id.toString() || ""}
          onChange={handleCurrencyChange}
        >
          {currencies.map((currency) => (
            <MenuItem key={currency.id} value={currency.id.toString()}>
              {currency.name} ({currency.abbreviation})
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <TextField
        label="قیمت به تومان"
        fullWidth
        value={amount}
        onChange={handleAmountChange}
        InputLabelProps={{
          style: { color: "#fff" },
        }}
        sx={{
          mb: 3,
          "& .MuiOutlinedInput-root": {
            "&.Mui-focused fieldset": {
              borderWidth: "1px",
              borderColor: "#fff",
            },
          },
        }}
      />

      <TextField
        label={`مقدار ${activeTab === 0 ? "" : ""} (${
          selectedCurrency?.abbreviation || ""
        })`}
        fullWidth
        value={receiveAmount}
        onChange={handleReceiveAmountChange}
        InputLabelProps={{
          style: { color: "#fff" },
        }}
        sx={{
          mb: 3,
          "& .MuiOutlinedInput-root": {
            "&.Mui-focused fieldset": {
              borderWidth: "1px",
              borderColor: "#fff",
            },
          },
        }}
      />

      <Typography
        variant="subtitle2"
        component="p"
        color="text.title"
        sx={{ mb: 1.5 }}
      >
        قیمت {activeTab === 0 ? "خرید" : "فروش"}:{" "}
        {activeTab === 0
          ? selectedCurrency?.buy || "نامشخص"
          : selectedCurrency?.sell || "نامشخص"}{" "}
        تومان
      </Typography>

      <Link to="https://panel.parsianbit.com/trade">
        <Button
          size={"large"}
          fullWidth
          variant={"contained"}
          sx={{
            bgcolor: "#58BC7C",
            color: "#202325",
            "&:hover": { bgcolor: "#58BC7C" },
          }}
        >
          {activeTab === 0 ? "خرید" : "فروش"} {selectedCurrency?.name}
        </Button>
      </Link>
    </Box>
  );
};

export default FiatForm;
